<template>
  <s-page class="max-w-3xl" v-if="isDataLoaded">
    <s-header class="px-3"> Insights </s-header>
    <div
      class="px-3 contain flex justify-start flex-col w-full h-auto bg-transparent"
    >
      <label
        class="mb-3 md:w-1/3 title-2 whitespace-normal overflow-visible text-left text-red-700 text-3xl sm:text-4xl tracking-tight whitespace-nowrap"
      >
        7-day Forcast
      </label>
      <ul
        class="mt-3 mb-6 mx-auto max-w-xl w-full active:mx-auto text-gray-800 text-xl md:text-2xl font-bold"
      >
        <li v-for="date in dates" :key="date.date" class="flex w-full">
          <span class="flex-1 text-left">{{ date.day }}</span>
          <span class="flex-1">{{ date.date }}</span>
          <span class="flex-1 text-right">{{ date.amount | usd }}</span>
        </li>
      </ul>
      <label
        class="mb-3 md:w-1/3 title-2 whitespace-normal overflow-visible text-left text-red-700 text-3xl sm:text-4xl tracking-tight whitespace-nowrap"
      >
        Current Week Projected Sales
      </label>
      <chart-projected-sales />
      <div class="flex justify-between items-center">
        <label
          class="my-3 md:w-1/3 title-2 whitespace-normal overflow-visible text-left text-red-700 text-3xl sm:text-4xl tracking-tight whitespace-nowrap"
        >
          Recipe Portion Costs
        </label>
        <s-button
          v-if="isManager || isAdmin"
          class="shadow-lg"
          @click="exportRecipesCsv"
        >
          Export Recipes
        </s-button>
      </div>
    </div>
    <section class="w-full flex flex-wrap items-center justify-center">
      <tile-cogs
        v-for="id in sortedRecipes"
        color="gray"
        :label="`${recipeName(id)} (${recipeUnitType(id)})`"
        :value="`${format(recipeUnitCostById(id))}`"
        :key="id"
        :id="id"
        @click="doIt($event)"
      />
    </section>
  </s-page>
</template>

<script>
import { mapGetters } from 'vuex'
import SPage from '@/components/SPage'
import SHeader from '@/components/SHeader'
import ChartProjectedSales from '@/components/ChartProjectedSales.vue'
import TileCogs from '@/components/TileCogs.vue'
import SButton from '@/components/Button'
import { DateTime } from 'luxon'
import { format } from '@/utils.js'
import { downloadCsv } from '@/utils'

export default {
  name: 'Insights',
  components: {
    SPage,
    SHeader,
    ChartProjectedSales,
    TileCogs,
    SButton
  },
  computed: {
    ...mapGetters('user', ['isManager', 'isAdmin']),
    ...mapGetters('global', ['storeNameById']),
    ...mapGetters('storeModule', ['id']),
    ...mapGetters('config', ['isDataLoaded']),
    ...mapGetters('storeModule/reports', ['reportsOrdered']),
    ...mapGetters('recipes', [
      'recipeIdsActive',
      'sortRecipeIdsByRecipeName',
      'recipeName',
      'recipeUnitType'
    ]),
    ...mapGetters('storeModule', ['recipeCostById', 'recipeUnitCostById']),
    sortedRecipes() {
      return this.sortRecipeIdsByRecipeName(this.recipeIdsActive)
    },
    dates() {
      let today = DateTime.utc().setZone('America/New_York').startOf('day')
      return Array.from({ length: 7 }, (v, k) => {
        return {
          day: today.plus({ days: k }).toFormat('ccc'),
          date: today.plus({ days: k }).toFormat('M/d'),
          amount: this.reportsOrdered[0].sales.totals[k]
        }
      })
    },
    routeName: function () {
      return this.$route.name
    },
    routeId: function () {
      return this.$route.params.id
    },
    currentStoreName() {
      return this.storeNameById(this.id) || 'unknown'
    }
  },
  methods: {
    format,
    doIt(id) {
      console.log(id)
      this.$modal.show('modal--edit-recipe', { id })
    },
    async exportRecipesCsv() {
      // Add logging to debug
      console.log('Active Recipe IDs:', this.recipeIdsActive)

      const recipes = this.recipeIdsActive
        .map((id) => {
          const recipe = this.$store.getters['recipes/recipeById'](id)
          console.log('Recipe:', id, recipe) // Debug log

          // Make sure recipe exists
          if (!recipe) return null

          // Get products data
          const products = Object.entries(recipe.products || {}).map(
            ([productId, data]) => {
              console.log('Product:', productId, data) // Debug log
              return {
                productId,
                productName:
                  this.$store.getters['storeModule/products/productName'](
                    productId
                  ),
                productCode: this.$store.getters[
                  'storeModule/products/productById'
                ](productId)
                  ? this.$store.getters['storeModule/products/productById'](
                      productId
                    ).code
                  : '',
                amount: data.amount || 0,
                unit: data.unit || '',
                cost: this.$store.getters[
                  'storeModule/productPriceByProductId'
                ](productId)
                  ? this.$store.getters['storeModule/productPriceByProductId'](
                      productId
                    ).cost
                  : 0
              }
            }
          )

          return {
            date: DateTime.now().toFormat('MM/dd/yyyy'),
            store: this.currentStoreName,
            recipeId: id,
            recipeName: this.recipeName(id) || '',
            recipeUnitType: this.recipeUnitType(id) || '',
            recipeCost: this.recipeCostById(id) || 0,
            recipeUnitCost: this.recipeUnitCostById(id) || 0,
            yieldAmount: recipe.yieldAmount || 0,
            yieldUnit: recipe.yieldUnit || '',
            products
          }
        })
        .filter((recipe) => recipe !== null) // Remove any null recipes

      console.log('Processed Recipes:', recipes) // Debug log

      // Only proceed if we have recipes
      if (!recipes.length) {
        console.error('No recipe data found')
        return
      }

      // Create CSV rows
      let rows = [
        [
          'Date',
          'Store',
          'Recipe ID',
          'Recipe Name',
          'Unit Type',
          'Total Cost',
          'Unit Cost'
          // 'Yield Amount',
          // 'Yield Unit',
          // 'Product ID',
          // 'Product Name',
          // 'Product Code',
          // 'Product Amount',
          // 'Product Unit',
          // 'Product Cost'
        ]
      ]

      // Add data rows
      recipes.forEach((recipe) => {
        if (!recipe.products.length) {
          // Add a row for recipe without products
          rows.push([
            recipe.date,
            recipe.store,
            recipe.recipeId,
            recipe.recipeName,
            recipe.recipeUnitType,
            format(recipe.recipeCost),
            format(recipe.recipeUnitCost)
            // recipe.yieldAmount,
            // recipe.yieldUnit,
            // '', // Empty product fields
            // '',
            // '',
            // '',
            // '',
            // ''
          ])
        } else {
          // Add rows for each product
          recipe.products.forEach((product) => {
            rows.push([
              recipe.date,
              recipe.store,
              recipe.recipeId,
              recipe.recipeName,
              recipe.recipeUnitType,
              format(recipe.recipeCost),
              format(recipe.recipeUnitCost)
              // recipe.yieldAmount,
              // recipe.yieldUnit,
              // product.productId,
              // product.productName,
              // product.productCode,
              // product.amount,
              // product.unit,
              // format(product.cost)
            ])
          })
        }
      })

      console.log('CSV Rows:', rows) // Debug log

      // Convert to CSV
      let outputCsv = ''
      rows.forEach((row) => {
        outputCsv += row.map((cell) => `"${cell || ''}"`).join(',')
        outputCsv += '\n'
      })

      // Generate filename
      const filename = `${DateTime.now().toFormat('yyyyMMdd')}_recipes_${
        this.currentStoreName
      }.csv`

      // Download CSV
      downloadCsv({
        data: outputCsv,
        filename: filename,
        mimeType: 'text/csv'
      })
    }
  }
}
</script>
